/**
 * Bis zu welchem Monat ein **kumulierter Jahresvergleich** rechnen darf.
 *
 * Der Vergleich „Wachstum gegenüber dem Vorjahreszeitraum" summiert beide
 * Jahre über dieselben Monate. Der laufende Monat ist dabei die eine Zelle,
 * die nicht passt: Im Vorjahr ist er vollständig gebucht, in diesem Jahr erst
 * angefangen. Am 7. September stünden also acht volle Monate plus eine Woche
 * gegen neun volle Monate — der Vergleich fiele um fast einen ganzen
 * Monatsumsatz zu niedrig aus, und zwar **jeden Monat aufs Neue**, am
 * stärksten am Monatsanfang. Ein Bereich, der auf Vorjahresniveau liegt, sähe
 * am 1. des Monats aus, als bräche er ein.
 *
 * **Warum nicht tagesgenau**, was die genauere Antwort wäre: Der Umsatz liegt
 * nur monatlich vor (`assignment_monthly_revenues.month_start`, gefüllt aus
 * dem Salesforce-Kontierungsmonat). Es gibt keine Tagesauflösung, die man
 * abschneiden könnte — anteilig hochzurechnen hieße, Umsatz zu erfinden, den
 * niemand gebucht hat. Deshalb die ehrlichere Variante: **nur abgeschlossene
 * Monate**, dafür beide Jahre gleich.
 *
 * @param jahr   Das angezeigte Jahr.
 * @param heute  Bezugszeitpunkt (injiziert, damit der Test nicht am Kalender hängt).
 * @returns 0-basierter Monatsindex (0 = Januar) oder `null`, wenn es keinen
 *          abgeschlossenen Monat gibt — im Januar des laufenden Jahres, und in
 *          jedem Jahr, das noch nicht begonnen hat.
 */
export function letzterAbgeschlossenerMonat(
  jahr: number,
  heute: Date,
): number | null {
  const laufendesJahr = heute.getFullYear();

  // Vergangenes Jahr: alle zwölf Monate sind gelaufen.
  if (jahr < laufendesJahr) return 11;

  // Zukunft: es gibt nichts zu vergleichen.
  if (jahr > laufendesJahr) return null;

  // Laufendes Jahr: der Vormonat ist der letzte vollständige.
  // getMonth() ist 0-basiert — im September (8) also August (7).
  const vormonat = heute.getMonth() - 1;
  return vormonat >= 0 ? vormonat : null;
}
